import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import DeleteCellV8 from 'shared-pages/components/buttons/delete-cell-v8';
import ButtonCopy from 'shared-pages/components/buttons/button-copy';
import { ImportExcel } from './import-from-excel';
import { isFotokiklosi } from 'shared-pages/common/constants';
import { Bullet } from 'shared-pages/components';

/* ----------- Grid ------------------ */

export const GridPage = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  console.log('selected', selected?.id);
  const [updateContainer] = useMutation(gqlUpdate);
  const [addNewContainer] = useMutation(gqlAdd);
  const [deleteContainer] = useMutation(gqlDelete);
  // ----   function to Copy Row into a new Record Collection ---------//
  const handleCopyRow = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Collection'), { id: -1 }),
        state: {
          ...location.state,
          copyFrom: row.original, // pass the row data for copying
          pathname: location.pathname,
        },
      });
    },
    [history, location, getRouteByName],
  );

  // ------------------ Row Clicked ------------------ //
  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Collection'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),

      {
        id: 'code',
        header: t('Κωδικός Εντύπου'),
        columnName: t('Κωδικός Εντύπου'),
        accessorKey: 'code',
        orderByField: 'code',
        cell: TableCellV8,
        size: 20,
        meta: { disabled: true },
      },

      {
        id: 'dateCollected',
        header: t('Ημερομηνία Συλλογής'),
        columnName: t('Ημερομηνία Συλλογής'),
        accessorKey: 'dateCollected',
        orderByField: 'dateCollected',
        cell: CellDateV8,
        size: 50,
        meta: { type: 'date' },
      },
      {
        id: 'finDispNote',
        header: t('Δελτίο Αποστολής'),
        columnName: t('Σημειώσεις'),
        accessorKey: 'finDispNote',
        orderByField: 'finDispNote',
        cell: ({ getValue, row }) => {
          const collectionNote = row.original.finDispNote;
          const lines = row.original.collectionLines || [];
          const lineNotes = lines
            .filter((line) => line.finDispNote)
            .map((line) => line.finDispNote);

          // Μοναδικές τιμές: το ίδιο Δ.Α. μπορεί να υπάρχει και στο header και στις γραμμές
          const allNotes = [];
          if (collectionNote) allNotes.push(collectionNote);
          allNotes.push(...lineNotes);
          const uniqueNotes = [...new Set(allNotes)];

          return (
            <div className="text-left d-flex">
              {uniqueNotes.map((note, i) => (
                <div key={i}>
                  {note}
                  {i !== uniqueNotes.length - 1 && ','}
                </div>
              ))}
            </div>
          );
        },
        size: 30,
        meta: { type: 'text' },
      },
      ...(!isGridInForm
        ? [
            {
              id: 'collectionPointId',
              header: t('Σημείο Συλλογής'),
              columnName: t('Σημείο Συλλογής'),
              accessorKey: 'collectionpoint.name',
              orderByField: 'collectionpoint.name',
              cell: ({ getValue, row }) => (
                <div className="text-left">
                  <div>{row.original.collectionpoint.name}</div>
                  <div style={{ color: 'gray', fontSize: 'smaller' }}>
                    {row.original.collectionpoint.addressLine1},&nbsp;
                    {row.original.collectionpoint.addressArea},&nbsp;
                    {row.original.collectionpoint.addressCity},&nbsp;
                    {row.original.collectionpoint.addressDistrict}
                  </div>
                </div>
              ),
              size: 35,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'collectorId',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'collector.name',
        orderByField: 'collector.name',
        cell: TableCellV8,
        size: 35,
        meta: { type: 'text', disabled: true },
      },
      {
        id: 'wastereceptionpointId',
        header: t('Προορισμός'),
        columnName: t('Προορισμός'),
        accessorKey: 'wastereceptionpoint.name',
        orderByField: 'wastereceptionpoint.name',
        cell: TableCellV8,
        size: 35,
        meta: { type: 'text', disabled: true },
      },
      {
        id: 'collectionLines',
        header: t('Απόβλητα'),
        columnName: t('Απόβλητα'),
        accessorKey: 'collectionLines',
        orderByField: 'collectionLines',
        cell: ({ getValue, row }) => {
          function compare(a, b) {
            if (a.aa < b.aa) {
              return -1;
            }
            if (a.aa > b.aa) {
              return 1;
            }
            return 0;
          }
          const lines = row.original.collectionLines;

          return (
            <div className="text-left">
              {lines.sort(compare).map((v, i, array) => {
                const prefix = array?.length > 1 ? <>{v?.aa}:</> : null;
                return (
                  <div key={i + v.aa}>
                    {prefix} {v?.material?.name} <Bullet />
                    &nbsp;{v?.qty} ΤΕΜ <Bullet /> {v?.netWeight} kg
                  </div>
                );
              })}
            </div>
          );
        },
        size: 35,
      },

      // isActiveColumn(t, i18n),
      // firstDateUserColumn(t),
      // lastDateUserColumn(t),

      ...(!isGridInForm && appStore.isPlatformOwner
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 100,
              cell: ({ row, table }) => {
                const isSelected = row.original.id === selected?.id;
                return true ? (
                  <div className="d-flex justify-content-start" style={{ gap: '2px' }}>
                    {/* {EditCellV8({ row, table })} */}
                    {!table.options.meta?.editedRows[row.id] && (
                      <DeleteCellV8 table={table} row={row} />
                    )}
                    {!table.options.meta?.editedRows[row.id] && (
                      <ButtonCopy
                        className="ms-1"
                        key={`${row.original.id}-${selected?.id}`}
                        selected={isSelected}
                        onClick={() => handleCopyRow(row)}
                        title={t('Αντιγραφή')}
                      />
                    )}
                  </div>
                ) : (
                  <>-</>
                );
              },
            },
          ]
        : []),
    ],
    [t, selected?.id],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key={`grid-${selected?.id ?? 'x'}`}
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          // meta prop removed as it is not supported by TablePaginatedV8
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Containers - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addContainertype.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε την Συλλογή <strong>${
                row?.code ?? ''
              }</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            console.log('row', row);
            const res = await deleteContainer({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε η Συλλογή');
            return true;
          }}
          injectFooterElement={
            isFotokiklosi && (appStore.isPlatformOwner || appStore.isCollector) ? (
              <ImportExcel refetch={refetch} queryParams={undefined}></ImportExcel>
            ) : (
              <></>
            )
          }
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
